/**
 * Session ↔ Ventes — Totaux et ventes espèces (écart, page sessions).
 * Fallback si worker_sessions_* et checkout utilisent des clés businessId différentes.
 */

import { parseSalesFromBusinessPayload } from './salesCacheSync';
import { dedupeSalesById, normalizeSaleTotal, sumCashSales } from './saleSessionHelpers';
import { getCashRegisterRepository } from '../app/di/container';

function sessionTimeBounds(session) {
  const loginMs = session?.loginTime ? new Date(session.loginTime).getTime() : 0;
  const logoutMs = session?.logoutTime
    ? new Date(session.logoutTime).getTime()
    : Date.now();
  return { loginMs, logoutMs };
}

function saleInSessionWindow(sale, session) {
  if (!sale || !session?.workerId) return false;
  if (String(sale.workerId) !== String(session.workerId)) return false;
  const t = new Date(sale.date || sale.createdAt || sale.timestamp || 0).getTime();
  if (!Number.isFinite(t) || t <= 0) return true;
  const { loginMs, logoutMs } = sessionTimeBounds(session);
  return t >= loginMs - 60000 && t <= logoutMs + 60000;
}

export function ventesForSession(session, businessId, preloadedSales) {
  if (!session || !businessId) return [];
  const salesList = Array.isArray(preloadedSales)
    ? preloadedSales
    : parseSalesFromBusinessPayload(businessId);
  return salesList.filter((s) => saleInSessionWindow(s, session));
}

export function cashSalesFromNcrTransactions(businessId, workerId) {
  try {
    const ncr = getCashRegisterRepository().getActiveSession(businessId);
    if (!ncr || String(ncr.workerId) !== String(workerId)) return 0;
    return (ncr.transactions || [])
      .filter((t) => t && t.type === 'sale')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  } catch {
    return 0;
  }
}

/**
 * Enrichit sales / totalSales / saleCount depuis ventes[] ou transactions caisse.
 * @param {object} session
 * @param {string} businessId
 * @param {object[]|null} preloadedSales — ventes déjà chargées (évite N lectures blob)
 */
export function enrichSessionWithVentes(session, businessId, preloadedSales = null) {
  if (!session || typeof session !== 'object') return session;

  let sales = dedupeSalesById(Array.isArray(session.sales) ? session.sales : []);
  let cashSales = sumCashSales(sales);

  // Nur ventes[] wenn Session leer — nie zu session.sales addieren (kein Doppelzählen)
  if (cashSales <= 0 && businessId) {
    const fromVentes = dedupeSalesById(
      ventesForSession(session, businessId, preloadedSales)
    );
    if (fromVentes.length > 0) {
      sales = fromVentes;
      cashSales = sumCashSales(fromVentes);
    }
  }

  if (cashSales <= 0 && businessId && session.workerId) {
    const cashFromNcr = cashSalesFromNcrTransactions(businessId, session.workerId);
    if (cashFromNcr > 0) {
      cashSales = cashFromNcr;
    }
  }

  const totalSales = sales.reduce(
    (sum, s) => sum + normalizeSaleTotal(s),
    0
  );

  return {
    ...session,
    sales,
    totalSales,
    saleCount: sales.length,
    cashSales
  };
}

/** Charge ventes une fois puis enrichit toutes les sessions (page Paramètres). */
export function enrichSessionsWithVentes(sessions, businessId) {
  if (!Array.isArray(sessions) || !businessId) return sessions || [];
  const preloadedSales = parseSalesFromBusinessPayload(businessId);
  return sessions.map((session) =>
    enrichSessionWithVentes(session, businessId, preloadedSales)
  );
}
